import React, { useMemo, useRef, useState } from 'react';

// Wraps an <input> or <textarea> and surfaces an @-mention popup of brand users.
// Inserts plain text "@<name> " at the caret on selection.
//
// Props:
//   as: 'input' | 'textarea'  (default 'input')
//   value, onChange(string), brandUsers, onKeyDown? — pass-through props go to the field
const MentionInput = React.forwardRef(({
  as = 'input',
  value,
  onChange,
  brandUsers = [],
  onKeyDown,
  className = '',
  ...rest
}, externalRef) => {
  const innerRef = useRef(null);
  const setRef = (el) => {
    innerRef.current = el;
    if (typeof externalRef === 'function') externalRef(el);
    else if (externalRef) externalRef.current = el;
  };

  const [popup, setPopup] = useState({ open: false, at: 0, filter: '', selected: 0 });

  const filtered = useMemo(() => {
    if (!popup.open) return [];
    const f = popup.filter.toLowerCase();
    return brandUsers
      .filter((u) => {
        const n = (u.name || '').toLowerCase();
        const e = (u.email || '').toLowerCase();
        return n.includes(f) || e.includes(f);
      })
      .slice(0, 6);
  }, [brandUsers, popup.open, popup.filter]);

  const recomputePopup = (val, caretPos) => {
    const beforeCaret = val.slice(0, caretPos);
    const lastAt = beforeCaret.lastIndexOf('@');
    if (lastAt < 0) {
      setPopup((p) => ({ ...p, open: false }));
      return;
    }
    const between = beforeCaret.slice(lastAt + 1);
    const charBefore = lastAt === 0 ? ' ' : val[lastAt - 1];
    if (!/\s|^$/.test(charBefore) || /\s/.test(between)) {
      setPopup((p) => ({ ...p, open: false }));
      return;
    }
    setPopup({ open: true, at: lastAt, filter: between, selected: 0 });
  };

  const handleChange = (e) => {
    const val = e.target.value;
    onChange(val);
    recomputePopup(val, e.target.selectionStart || 0);
  };

  const insertMention = (user) => {
    const name = user.name || user.email;
    const before = value.slice(0, popup.at);
    const after = value.slice(popup.at + 1 + popup.filter.length);
    const newVal = before + '@' + name + ' ' + after;
    onChange(newVal);
    setPopup((p) => ({ ...p, open: false }));
    setTimeout(() => {
      const newPos = before.length + 1 + name.length + 1;
      const el = innerRef.current;
      if (el) {
        el.focus();
        try { el.setSelectionRange(newPos, newPos); } catch (_) {}
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (popup.open && filtered.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setPopup((p) => ({ ...p, selected: (p.selected + 1) % filtered.length }));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setPopup((p) => ({ ...p, selected: (p.selected - 1 + filtered.length) % filtered.length }));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(filtered[popup.selected]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setPopup((p) => ({ ...p, open: false }));
        return;
      }
    }
    if (onKeyDown) onKeyDown(e);
  };

  const fieldProps = {
    ref: setRef,
    value,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    onBlur: () => setTimeout(() => setPopup((p) => ({ ...p, open: false })), 120),
    className,
    ...rest,
  };

  return (
    <div className="relative">
      {as === 'textarea' ? <textarea {...fieldProps} /> : <input {...fieldProps} />}
      {popup.open && filtered.length > 0 && (
        <div className="absolute left-0 z-30 mt-1 min-w-[14rem] max-w-xs bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {filtered.map((u, i) => (
            <div
              key={u.id}
              onMouseDown={(e) => { e.preventDefault(); insertMention(u); }}
              className={`px-3 py-2 cursor-pointer text-sm ${i === popup.selected ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50 text-gray-700'}`}
            >
              <div className="font-medium truncate">{u.name || u.email}</div>
              {u.name && <div className="text-[11px] text-gray-400 truncate">{u.email}</div>}
              {u.role === 'OWNER' && <div className="text-[10px] text-gray-400 mt-0.5">Owner</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

// Render a string with @mentions highlighted.
export const renderWithMentions = (text, brandUsers = []) => {
  if (!text) return null;
  const names = new Set(brandUsers.map((u) => u.name || u.email).filter(Boolean));
  // simple split: @<word(s)> — we match as much as possible by trying longest match
  const tokens = [];
  let i = 0;
  while (i < text.length) {
    if (text[i] === '@' && (i === 0 || /\s/.test(text[i - 1]))) {
      // try to match the longest known name starting after @
      let matched = null;
      for (const n of names) {
        if (text.slice(i + 1, i + 1 + n.length) === n) {
          if (!matched || n.length > matched.length) matched = n;
        }
      }
      if (matched) {
        tokens.push(<span key={i} className="text-blue-600 font-medium">@{matched}</span>);
        i += 1 + matched.length;
        continue;
      }
    }
    // accumulate plain text until next @
    let next = text.indexOf('@', i + 1);
    if (next === -1) next = text.length;
    tokens.push(text.slice(i, next));
    i = next;
  }
  return tokens;
};

export default MentionInput;
